import { Injectable, Scopes, ControllerProtocol } from '@visiba/instantiation';
import { signal } from '@preact/signals-react';
import { AnalyticCategories, AnalyticService } from '@application/analytic/analytic.service';
import { BookingScreen, ViewBookingAnalyticActions, ViewBookingController } from '../view_booking.controller';
import type { ClientPhysicalLocationApiModel } from '@api_patient/generated/clientv2/models/client-physical-location-api-model';
import { TimeSlotApiService } from '@api_patient/timeslot_api.service';
import { BookingBasketService } from '../booking_basket.service';
import { UniversalConfig } from '@application/universal_config.service';
import { ClientTimeslotApiModel } from '@api_patient/generated/clientv2/models';
import { AsyncSignalValue } from '@component/async.component';
import { DateService } from '@application/time/date.service';
import type { Dayjs } from 'dayjs';
import { I18nAndL10n } from '@application/i18n/mod';

type LocationType = 'digital' | 'physical';

export interface Location {
	id: number;
	type: LocationType;
	name: string;
	meta: string;
	nextAvailableTime: Dayjs | null;
}

@Injectable({
	scope: Scopes.Scoped,
})
export class ScreenLocationController implements ControllerProtocol {
	public screenState = signal<'ready' | 'not-ready' | 'busy'>('not-ready');
	public locations = signal<AsyncSignalValue<Location[]>>({
		flowState: 'pending',
		value: null,
	});
	public locationAvailability: ClientTimeslotApiModel[] = [];

	constructor(
		private readonly viewBookingController: ViewBookingController,
		private readonly universalConfig: UniversalConfig,
		private readonly timeSlotApiService: TimeSlotApiService,
		private readonly bookingBasketService: BookingBasketService,
		private readonly analyticService: AnalyticService,
		private readonly dateService: DateService,
	) {}

	async whenMount() {
		this.analyticService.trackPageView({
			customTitle: 'Booking: Location',
		});

		this.bookingBasketService.unlockTimeSlot();

		if (!this.allowedToBeHere()) {
			this.analyticService.trackEvent(AnalyticCategories.Booking, ViewBookingAnalyticActions.NotAllowedToBeHere, 'Location');
			this.viewBookingController.goToScreen(BookingScreen.ReasonForVisit, { replace: true });

			return;
		}

		const flowSettings = this.viewBookingController.getFlowSettings();

		if (flowSettings.locationId) {
			this.analyticService.trackEvent(
				AnalyticCategories.Booking,
				ViewBookingAnalyticActions.AutoSetLocation,
				`${flowSettings.locationId}`,
			);
			this.viewBookingController.goToScreen(BookingScreen.TimeSlot, {
				replace: true,
				locationType: 'physical',
				locationId: flowSettings.locationId,
			});

			return;
		}

		if (flowSettings.locationType === 'digital') {
			this.analyticService.trackEvent(AnalyticCategories.Booking, ViewBookingAnalyticActions.AutoSetLocation, 'Digital');
			this.viewBookingController.goToScreen(BookingScreen.TimeSlot, { replace: true, locationType: 'digital' });

			return;
		}

		let fromDate: Dayjs | null = flowSettings.fromDate;
		let toDate: Dayjs | null = flowSettings.toDate;

		if (
			flowSettings.searchContext?.FirstSearchDate == null &&
			flowSettings.searchContext?.RelativeStart == null &&
			flowSettings.fromDate == null
		) {
			fromDate = this.dateService.newDate();
		}

		if (
			flowSettings.searchContext?.LastSearchDate == null &&
			flowSettings.searchContext?.RelativeEnd == null &&
			flowSettings.toDate == null
		) {
			toDate = (fromDate ?? this.dateService.newDate()).add(1, 'year');
		}

		// Check for available time slots one year ahead
		const availabilityResult = await this.timeSlotApiService.availableTimeSlots(this.universalConfig.selectedUnitId, {
			searchContextId: flowSettings.searchContextId ?? undefined,
			conditionId: this.bookingBasketService.get().reasonForVisit ?? undefined,
			fromDate: fromDate?.format('YYYY-MM-DD'),
			toDate: toDate?.format('YYYY-MM-DD'),
			filterableUnitId: flowSettings.filterUnitId ?? undefined,
			resourceId: flowSettings.filterResourceId ?? undefined,
			specialityId: flowSettings.filterSpecialityId ?? undefined,
		});

		if (availabilityResult.err) {
			this.analyticService.trackEvent(
				AnalyticCategories.Booking,
				ViewBookingAnalyticActions.ErrorFetchingAvailability,
				availabilityResult.err.message,
			);

			this.viewBookingController.goToScreen(BookingScreen.Error);

			return;
		}

		const locations: Location[] = [];

		if (availabilityResult.ok.data.Digital.FirstAvailableUpcomingTime && flowSettings.locationType !== 'physical') {
			locations.push({
				id: -1,
				type: 'digital',
				name: I18nAndL10n.translate('View.Booking.ScreenLocation.Digital.Title'),
				meta: I18nAndL10n.translate('View.Booking.ScreenLocation.Digital.Description'),
				nextAvailableTime: this.dateService.newDate(availabilityResult.ok.data.Digital.FirstAvailableUpcomingTime),
			});
		}

		this.viewBookingController.flowData.peek().value?.physicalLocations.map((location) => {
			const firstAvailableUpcomingTime = availabilityResult.ok.data.PhysicalLocations[location.Id]?.FirstAvailableUpcomingTime;

			if (firstAvailableUpcomingTime) {
				locations.push({
					id: location.Id,
					type: 'physical',
					name: location.Name ?? '',
					meta: `${location.Address}, ${location.PostalCode} ${location.City}`,
					nextAvailableTime: this.dateService.newDate(firstAvailableUpcomingTime),
				});
			}
		});

		if (
			locations.find((location) => location.type === 'digital') &&
			locations.find((location) => location.type === 'physical') == null
		) {
			this.analyticService.trackEvent(AnalyticCategories.Booking, ViewBookingAnalyticActions.OnlyDigital);
			this.analyticService.trackEvent(AnalyticCategories.Booking, ViewBookingAnalyticActions.AutoSetLocation, 'Digital');
			this.viewBookingController.goToScreen(BookingScreen.TimeSlot, { replace: true, locationType: 'digital' });

			return;
		}

		this.locations.value = {
			flowState: 'accessible',
			value: locations,
		};
	}

	public getLocation(locationId: number): ClientPhysicalLocationApiModel | null {
		return this.viewBookingController.flowData.peek().value?.physicalLocations.find((location) => location.Id === locationId) ?? null;
	}

	public selectLocation(location: Location) {
		this.bookingBasketService.set({
			currentDate: null,
		});

		if (location.type === 'digital') {
			this.viewBookingController.goToScreen(BookingScreen.TimeSlot, { locationType: 'digital' });
		} else {
			this.viewBookingController.goToScreen(BookingScreen.TimeSlot, { locationType: 'physical', locationId: location.id });
		}
	}

	private allowedToBeHere() {
		// If we don't have flow data, we can't be here.
		const flowData = this.viewBookingController.flowData.peek().value;
		if (!flowData) {
			return false;
		}

		// If we don't have a selected respondent type, we can't be here.
		if (this.bookingBasketService.get().respondantType == null) {
			return false;
		}

		// If we have reason for visits, we need to have a selected reason for visit.
		if (
			flowData.useReasonForVisits &&
			this.viewBookingController.getFilteredReasonForVisits().length > 0 &&
			!this.viewBookingController.getSelectedReasonForVisit()
		) {
			return false;
		}

		return true;
	}
}
